import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail } from 'lucide-react';

const Contact = () => {
  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>Contact Us | Schedule a Consultation – Valentis Law Group</title>
        <meta name="description" content="Get in touch with Valentis Law Group to schedule a consultation for your international dispute resolution, arbitration, or commercial litigation matter." />
        <link rel="canonical" href="https://valentislawgroup.com/contact" />
        <meta property="og:title" content="Contact Us | Schedule a Consultation – Valentis Law Group" />
        <meta property="og:description" content="Get in touch with Valentis Law Group to schedule a consultation for your international dispute resolution, arbitration, or commercial litigation matter." />
        <meta property="og:url" content="https://valentislawgroup.com/contact" />
        <meta name="twitter:title" content="Contact Us | Schedule a Consultation – Valentis Law Group" />
        <meta name="twitter:description" content="Get in touch with Valentis Law Group to schedule a consultation for your international dispute resolution, arbitration, or commercial litigation matter." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl text-center mx-auto">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">Schedule a Consultation</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              Book Your Appointment
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              Schedule a consultation with VALENTIS LAW GROUP to discuss your international dispute resolution and enforcement matters.
            </p>
          </div>
        </div>
      </section>

      {/* Calendly Embed Section */}
      <section className="py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="bg-ink/5 backdrop-blur-sm border border-ink/10 rounded-lg overflow-hidden">
            {/* Calendly Embed */}
            <div className="w-full h-[700px]">
              <iframe
                src="https://calendly.com/contact-valentislaw/30min"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule a consultation with Valentis Law Group"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Email */}
            <div className="bg-ink/5 backdrop-blur-sm border border-ink/10 rounded-lg p-8 text-center">
              <Mail size={48} className="text-ink/60 mx-auto mb-6" />
              <h3 className="font-serif text-2xl mb-4">Email Us</h3>
              <a
                href="mailto:contact@valentislaw.com"
                className="text-xl font-medium text-ink hover:text-ink/80 transition-colors duration-300"
              >
                contact@valentislaw.com
              </a>
            </div>

            {/* Phone */}
            <div className="bg-ink/5 backdrop-blur-sm border border-ink/10 rounded-lg p-8 text-center">
              <svg className="w-12 h-12 text-ink/60 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <h3 className="font-serif text-2xl mb-4">Call Us</h3>
              <a
                href="tel:+919654234196"
                className="text-xl font-medium text-ink hover:text-ink/80 transition-colors duration-300"
              >
                +91 96542 34196
              </a>
            </div>
          </div>

          {/* Office Address */}
          <div className="mt-8 bg-ink/5 backdrop-blur-sm border border-ink/10 rounded-lg p-8 lg:p-12 text-center">
            <svg className="w-12 h-12 text-ink/60 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="font-serif text-3xl md:text-4xl mb-4">Our Office</h3>
            <p className="text-lg text-ink/70 mb-2">
              STS 001, DLF Star Tower
            </p>
            <p className="text-lg text-ink/70">
              Sector 30, Gurugram 122001, India
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

/* COMMENTED OUT - Original Contact Form Content
import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    practiceArea: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent Successfully",
      description: "Thank you for contacting VALENTIS LAW GROUP. We'll respond within 24 hours.",
    });
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      practiceArea: '',
      message: ''
    });
  };

  return (
    <div className="bg-cream text-ink">
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">Contact Us</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              Let's Discuss Your Legal Needs
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              Reach out to VALENTIS LAW GROUP to schedule a consultation or learn more about how our experienced attorneys can support your objectives.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-4xl mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                ... (form fields) ...
              </form>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="font-serif text-3xl mb-6">Get in Touch</h3>
                <p className="text-ink/70 leading-relaxed">
                  Our team is available to discuss your legal needs and provide strategic counsel.
                </p>
              </div>
              ... (contact info cards) ...
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-6">Global Presence</h2>
            <p className="text-lg text-ink/70">VALENTIS LAW GROUP maintains offices in key financial centers worldwide</p>
          </div>
          ... (office locations) ...
        </div>
      </section>
    </div>
  );
};
*/